import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { ArrowLeft, ArrowRight, CheckCircle, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/_core/hooks/useAuth";
import { LoginPrompt } from "@/components/auth/AuthDialog";
import HelioLogoMark from "@/components/HelioLogoMark";
import { Button } from "@/components/ui/button";
import { openDashboardPath } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import SurveyPhotoSlot from "@/components/survey/SurveyPhotoSlot";
import BillUploadDropzone from "@/components/quote/BillUploadDropzone";
import {
  useDeleteSurveyPhoto,
  useInstallerSurvey,
  useRemoteSurvey,
  useSaveRemoteSurvey,
  useSubmitRemoteSurvey,
  useUploadInstallerSurveyBill,
  useUploadSurveyPhoto,
} from "@/api/hooks";
import { ApiError } from "@/api/client";
import type { RemoteSurveyPayload } from "@/api/types";
import {
  REMOTE_SURVEY,
  SURVEY_SECTIONS,
  emptySurveySections,
  photoTypesForSection,
  type CapturePath,
  type PhotoType,
  type SurveySectionKey,
  type SurveySections,
} from "@/constants/remoteSurvey";
import {
  trackSurveyPhoto,
  trackSurveySave,
  trackSurveySection,
  trackSurveyStart,
  trackSurveySubmit,
} from "@/analytics";
import { photoUploadRequirements } from "@/utils/surveyPhoto";

function asBool(value: unknown): boolean | null {
  if (value === true || value === "yes" || value === 1 || value === "1") return true;
  if (value === false || value === "no" || value === 0 || value === "0") return false;
  return null;
}

function applyBillToProperty(
  prev: SurveySections,
  saved: RemoteSurveyPayload,
): SurveySections {
  const incoming = saved.sections?.property ?? {};
  const nextAddress =
    prev.property.address?.trim()
      ? prev.property.address
      : typeof incoming.address === "string"
        ? incoming.address
        : prev.property.address;
  const nextPostcode =
    prev.property.postcode?.trim()
      ? prev.property.postcode
      : typeof incoming.postcode === "string"
        ? incoming.postcode
        : prev.property.postcode;

  return {
    ...prev,
    property: {
      ...prev.property,
      address: nextAddress,
      postcode: nextPostcode,
    },
  };
}

function mergeSections(remote?: RemoteSurveyPayload | null): SurveySections {
  const base = emptySurveySections();
  if (!remote?.sections) return base;
  for (const key of SURVEY_SECTIONS) {
    const incoming = remote.sections[key];
    if (incoming && typeof incoming === "object") {
      (base as Record<string, unknown>)[key] = { ...base[key], ...incoming };
    }
  }
  if (remote.prefill?.address && !base.property.address) base.property.address = remote.prefill.address;
  if (remote.prefill?.postcode && !base.property.postcode) base.property.postcode = remote.prefill.postcode;
  return base;
}

function formatSavedAt(iso?: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function YesNo({
  value,
  onChange,
  name,
}: {
  value: boolean | null;
  onChange: (next: boolean) => void;
  name: string;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {[true, false].map((option) => (
        <button
          key={String(option)}
          type="button"
          onClick={() => onChange(option)}
          className={`rounded-lg border px-3 py-2 text-sm ${
            value === option
              ? "border-[#29ffa9] bg-[#29ffa9]/10 text-[#29ffa9]"
              : "border-white/15 text-white/70 hover:border-white/30"
          }`}
        >
          {option ? "Yes" : "No"}
        </button>
      ))}
      <span className="sr-only">{name}</span>
    </div>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-red-400 mt-1">{message}</p>;
}

export default function RemoteSiteSurveyPage() {
  const [, installerParams] = useRoute("/installer/surveys/:id");
  const [, customerParams] = useRoute("/portal/survey/:projectId");
  const surveyId = Number(installerParams?.id);
  const projectId = Number(customerParams?.projectId);
  if (Number.isFinite(surveyId) && surveyId > 0) {
    return <RemoteSiteSurvey surveyId={surveyId} />;
  }
  if (Number.isFinite(projectId) && projectId > 0) {
    return <RemoteSiteSurvey projectId={projectId} />;
  }
  return (
    <div className="min-h-screen bg-[#050d1a] text-white flex items-center justify-center">Survey not found.</div>
  );
}

function RemoteSiteSurvey({ projectId, surveyId }: { projectId?: number; surveyId?: number }) {
  const [, navigate] = useLocation();
  const { loading, isAuthenticated, user } = useAuth();
  const standaloneRoute = Boolean(surveyId);
  const projectQuery = useRemoteSurvey(projectId, isAuthenticated && !standaloneRoute);
  const installerQuery = useInstallerSurvey(surveyId, isAuthenticated && standaloneRoute);
  const { data, isLoading, error } = standaloneRoute ? installerQuery : projectQuery;
  const saveMutation = useSaveRemoteSurvey(projectId ?? 0, surveyId);
  const uploadMutation = useUploadSurveyPhoto(projectId ?? 0, surveyId);
  const deleteMutation = useDeleteSurveyPhoto(projectId ?? 0, surveyId);
  const submitMutation = useSubmitRemoteSurvey(projectId ?? 0, surveyId);
  const billMutation = useUploadInstallerSurveyBill(surveyId);
  const trackingId = String(surveyId ?? projectId ?? "");

  const [sections, setSections] = useState<SurveySections>(emptySurveySections());
  const [notes, setNotes] = useState("");
  const [sectionIndex, setSectionIndex] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [hydrated, setHydrated] = useState(false);
  const [billFile, setBillFile] = useState<File | null>(null);
  const lastSavedAt = data?.lastSavedAt ?? null;
  const dirtyRef = useRef(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!data || hydrated) return;
    setSections(mergeSections(data));
    setNotes(data.customerNotes ?? "");
    const hideDeclarationOnLoad = Boolean(data.standalone);
    const keys = hideDeclarationOnLoad
      ? SURVEY_SECTIONS.filter((key) => key !== "declaration")
      : SURVEY_SECTIONS;
    const idx = keys.indexOf(data.currentSection as SurveySectionKey);
    setSectionIndex(idx >= 0 ? idx : 0);
    setHydrated(true);
    if (!startedRef.current) {
      startedRef.current = true;
      trackSurveyStart(trackingId, data.status);
    }
  }, [data, hydrated, trackingId]);

  const hideDeclaration = data ? Boolean(data.standalone) : standaloneRoute;
  const wizardSections = hideDeclaration
    ? SURVEY_SECTIONS.filter((key) => key !== "declaration")
    : SURVEY_SECTIONS;
  const sectionKey = wizardSections[Math.min(sectionIndex, wizardSections.length - 1)] ?? "property";
  const editableSectionKey = (data?.editableSections ?? []).join("|");
  const editableSections = useMemo(
    () => new Set(editableSectionKey === "" ? [] : editableSectionKey.split("|")),
    [editableSectionKey],
  );
  const frozen = hydrated && editableSections.size === 0;
  const canEditCurrent = editableSections.has(sectionKey);
  const addressLocked = !editableSections.has("property") || (!data?.standalone && !data?.attachedAt);
  const alreadySubmitted = Boolean(data?.submittedAt) || data?.status === "submitted" || Boolean(data?.pack);
  const loftAccessible = asBool(sections.loft.accessible);
  const existingSolar = asBool(sections.electrical.existing_solar);
  const existingBattery = asBool(sections.electrical.existing_battery);
  const existingEvHeatPump = asBool(sections.electrical.existing_ev_heat_pump);
  const existingGeneration = existingSolar === true
    || existingBattery === true
    || existingEvHeatPump === true
    || asBool(sections.electrical.existing_generation);

  const photoTypes = useMemo(
    () => photoTypesForSection(sectionKey, {
      loftAccessible,
      existingGeneration,
      existingSolar,
      existingBattery,
      existingEvHeatPump,
    }),
    [sectionKey, loftAccessible, existingGeneration, existingSolar, existingBattery, existingEvHeatPump],
  );

  const imagesByType = useMemo(() => {
    const map = new Map<string, NonNullable<typeof data>["images"][number]>();
    for (const image of data?.images ?? []) map.set(image.photoType, image);
    return map;
  }, [data?.images]);

  const persist = useCallback(
    async (nextSection?: SurveySectionKey, idle = false) => {
      const payload = {
        sections: Object.fromEntries(
          Object.entries(sections).filter(([key]) => editableSections.has(key)),
        ) as Record<string, Record<string, unknown>>,
        current_section: nextSection ?? sectionKey,
        idle,
        ...(editableSections.has("declaration") ? { customer_notes: notes } : {}),
      };
      const saved = await saveMutation.mutateAsync(payload);
      dirtyRef.current = false;
      trackSurveySave(trackingId, nextSection ?? sectionKey, idle);
      return saved;
    },
    [editableSections, notes, saveMutation, sectionKey, sections, trackingId],
  );

  useEffect(() => {
    if (!hydrated || frozen || !canEditCurrent || billMutation.isPending) return;
    const idle = window.setInterval(() => {
      if (dirtyRef.current) void persist(sectionKey, true).catch(() => undefined);
    }, 30_000);
    return () => window.clearInterval(idle);
  }, [billMutation.isPending, canEditCurrent, frozen, hydrated, persist, sectionKey]);

  useEffect(() => {
    if (!hydrated || frozen || !canEditCurrent || !dirtyRef.current || billMutation.isPending) return;
    const handle = window.setTimeout(() => {
      void persist(sectionKey, true).catch(() => undefined);
    }, 2000);
    return () => window.clearTimeout(handle);
  }, [billMutation.isPending, canEditCurrent, frozen, hydrated, notes, persist, sectionKey, sections]);

  const updateSection = <K extends SurveySectionKey>(key: K, patch: Partial<SurveySections[K]>) => {
    dirtyRef.current = true;
    setSections((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const uploadBill = async (file: File) => {
    if (!surveyId) return;
    setBillFile(file);
    try {
      const saved = await billMutation.mutateAsync(file);
      setSections((prev) => applyBillToProperty(prev, saved));
      toast.success("Bill details applied. Check address and usage.");
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Could not read that bill. Enter details manually.";
      toast.error(message);
    }
  };

  const validateSection = (key: SurveySectionKey): boolean => {
    const errors: Record<string, string> = {};
    if (key === "property") {
      if (hideDeclaration) {
        if (!sections.property.address?.trim()) errors.address = "Enter the property address.";
        if (!sections.property.postcode?.trim()) errors.postcode = "Enter the postcode.";
      }
      if (!sections.property.property_type) errors.property_type = "Choose a property type.";
      if (!sections.property.storeys) errors.storeys = "Choose the number of storeys.";
      if (!sections.property.age_band) errors.age_band = "Choose an age band.";
      if (sections.property.extensions === null) errors.extensions = "Required.";
      if (sections.property.loft_conversion === null) errors.loft_conversion = "Required.";
    }
    if (key === "loft" && loftAccessible === null) errors.loft_accessible = "Tell us if the loft is accessible.";
    if (key === "electrical") {
      for (const field of ["existing_solar", "existing_battery", "existing_ev_heat_pump"] as const) {
        if (sections.electrical[field] === null) errors[field] = "Required.";
      }
    }
    if (key === "battery") {
      if (!sections.battery.location) errors.battery_location = "Choose a location.";
      for (const field of ["dry", "ventilated", "accessible", "not_living_accommodation", "combustibles_nearby", "heat_source_nearby"] as const) {
        if (sections.battery[field] === null) errors[field] = "Required.";
      }
    }
    if (key === "inverter") {
      if (!sections.inverter.preferred_location) errors.preferred_location = "Choose a location.";
      if (!sections.inverter.indoor_outdoor) errors.indoor_outdoor = "Required.";
    }
    if (key === "system" && sections.system.confirmed === null && !hideDeclaration) errors.system_confirmed = "Please confirm.";
    if (key === "declaration") {
      if (!sections.declaration.statement_accurate || !sections.declaration.statement_access || !sections.declaration.statement_changes || !sections.declaration.accepted) {
        errors.declaration = "Please confirm all statements.";
      }
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const homePath = standaloneRoute ? "/installer/surveys" : "/portal";
  const leaveSurvey = () => {
    if (standaloneRoute && user?.role === "admin") {
      void openDashboardPath(data?.standalone ? "/admin/field-surveys" : "/admin/projects");
      return;
    }
    navigate(homePath);
  };

  const goNext = async () => {
    if (!validateSection(sectionKey)) return;
    const next = Math.min(sectionIndex + 1, wizardSections.length - 1);
    try {
      await persist(wizardSections[next]);
      setSectionIndex(next);
      trackSurveySection(trackingId, wizardSections[next]);
    } catch {
      toast.error("Could not save. Check your connection and try again.");
    }
  };

  const goBack = async () => {
    const prev = Math.max(sectionIndex - 1, 0);
    try {
      await persist(wizardSections[prev]);
    } catch {
      // still allow navigation
    }
    setSectionIndex(prev);
  };

  const handleSubmit = async () => {
    const lastKey = wizardSections[wizardSections.length - 1];
    if (!validateSection(hideDeclaration ? lastKey : "declaration")) return;
    try {
      await persist(hideDeclaration ? lastKey : "declaration");
      await submitMutation.mutateAsync();
      trackSurveySubmit(trackingId);
      const updated = alreadySubmitted && !hideDeclaration;
      toast.success(
        hideDeclaration
          ? (alreadySubmitted ? "Field survey updated." : "Field survey submitted. Attach it to a quote when one exists.")
          : (updated ? "Survey updated. The installer pack has been refreshed." : "Survey submitted. Your installer has the pack."),
      );
      leaveSurvey();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Could not submit the survey.";
      toast.error(message);
    }
  };

  if (loading || (isAuthenticated && isLoading && !hydrated)) {
    return (
      <div className="min-h-screen bg-[#050d1a] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#29ffa9] animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050d1a] text-white">
        <LoginPrompt message="Sign in to continue your site survey." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050d1a] text-white flex flex-col items-center justify-center gap-4">
        <p>We could not load this survey.</p>
        <Button className="btn-cta" onClick={leaveSurvey}>Back to portal</Button>
      </div>
    );
  }

  const requiredCount = photoTypes.length;
  const remaining = photoTypes.filter((type) => !imagesByType.has(type)).length;

  return (
    <div className="min-h-screen bg-[#050d1a] text-white">
      <header className="sticky top-0 z-20 border-b border-white/8 bg-[#050d1a]/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-2">
            <HelioLogoMark className="text-base" />
            <span className="text-sm font-semibold">Site survey</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-white/50">
            {lastSavedAt && (
              <span className="flex items-center gap-1">
                <Save className="h-3.5 w-3.5" />
                Saved at {formatSavedAt(lastSavedAt)}
              </span>
            )}
            <button type="button" className="text-white/70 hover:text-white" onClick={leaveSurvey}>
              Complete later
            </button>
          </div>
        </div>
        <div className="mx-auto max-w-3xl px-4 pb-3">
          <div className="flex gap-1 overflow-x-auto">
            {wizardSections.map((key, i) => (
              <div
                key={key}
                className={`h-1.5 min-w-[28px] flex-1 rounded-full ${
                  i < sectionIndex ? "bg-[#29ffa9]" : i === sectionIndex ? "bg-[#29ffa9]/60" : "bg-white/10"
                }`}
                title={REMOTE_SURVEY.sectionLabels[key]}
              />
            ))}
          </div>
          <p className="text-xs text-white/40 mt-2">
            {sectionIndex + 1} / {wizardSections.length} · {REMOTE_SURVEY.sectionLabels[sectionKey]}
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-6 pb-32 space-y-6">
        {frozen && (
          <div className="rounded-xl border border-[#29ffa9]/30 bg-[#29ffa9]/10 p-4 text-sm">
            This survey has been submitted. You can review the answers below.
          </div>
        )}

        {sectionKey === "property" && (
          <section className="space-y-4">
            <h1 className="font-display text-2xl font-semibold">Property</h1>
            {hideDeclaration && canEditCurrent && (
              <div className="space-y-3">
                <p className="text-sm text-white/50">
                  Optional — upload a utility bill to fill address and usage. You can still type them in below.
                </p>
                <div className={billMutation.isPending ? "pointer-events-none opacity-60" : ""}>
                  <BillUploadDropzone
                    onFileSelected={(file) => void uploadBill(file)}
                    selectedFile={billFile}
                    variant="compact"
                    tone="dark"
                  />
                </div>
                {billMutation.isPending && (
                  <p className="flex items-center gap-2 text-sm text-white/60">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Reading your bill…
                  </p>
                )}
              </div>
            )}
            <div>
              <Label className="text-white/70">Address</Label>
              <Input
                className="mt-1 bg-[#132035] border-white/10 text-white"
                value={sections.property.address ?? ""}
                readOnly={addressLocked}
                onChange={(e) => updateSection("property", { address: e.target.value })}
              />
              <FieldError message={fieldErrors.address} />
            </div>
            <div>
              <Label className="text-white/70">Postcode</Label>
              <Input
                className="mt-1 bg-[#132035] border-white/10 text-white"
                value={sections.property.postcode ?? ""}
                readOnly={addressLocked}
                onChange={(e) => updateSection("property", { postcode: e.target.value })}
              />
              <FieldError message={fieldErrors.postcode} />
            </div>
            <div>
              <Label className="text-white/70">Property type</Label>
              <select
                className="mt-1 w-full rounded-md border border-white/10 bg-[#132035] px-3 py-2 text-sm"
                value={sections.property.property_type ?? ""}
                onChange={(e) => updateSection("property", { property_type: e.target.value || null })}
              >
                <option value="">Select</option>
                {REMOTE_SURVEY.propertyTypes.map((type) => (
                  <option key={type} value={type}>{REMOTE_SURVEY.propertyTypeLabels[type]}</option>
                ))}
              </select>
              <FieldError message={fieldErrors.property_type} />
            </div>
            <div>
              <Label className="text-white/70">Storeys</Label>
              <select
                className="mt-1 w-full rounded-md border border-white/10 bg-[#132035] px-3 py-2 text-sm"
                value={sections.property.storeys ?? ""}
                onChange={(e) => updateSection("property", { storeys: e.target.value || null })}
              >
                <option value="">Select</option>
                {REMOTE_SURVEY.storeys.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <FieldError message={fieldErrors.storeys} />
            </div>
            <div>
              <Label className="text-white/70">Age band</Label>
              <select
                className="mt-1 w-full rounded-md border border-white/10 bg-[#132035] px-3 py-2 text-sm"
                value={sections.property.age_band ?? ""}
                onChange={(e) => updateSection("property", { age_band: e.target.value || null })}
              >
                <option value="">Select</option>
                {REMOTE_SURVEY.ageBands.map((band) => (
                  <option key={band} value={band}>{REMOTE_SURVEY.ageBandLabels[band]}</option>
                ))}
              </select>
              <FieldError message={fieldErrors.age_band} />
            </div>
            <div>
              <Label className="text-white/70 mb-2 block">Extensions?</Label>
              <YesNo name="extensions" value={asBool(sections.property.extensions)} onChange={(v) => updateSection("property", { extensions: v })} />
              <FieldError message={fieldErrors.extensions} />
            </div>
            <div>
              <Label className="text-white/70 mb-2 block">Loft conversion?</Label>
              <YesNo name="loft_conversion" value={asBool(sections.property.loft_conversion)} onChange={(v) => updateSection("property", { loft_conversion: v })} />
              <FieldError message={fieldErrors.loft_conversion} />
            </div>
          </section>
        )}

        {sectionKey === "access" && (
          <section className="space-y-4">
            <h1 className="font-display text-2xl font-semibold">Access</h1>
            <p className="text-sm text-white/50">Tick any access that is restricted or needs a note.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {REMOTE_SURVEY.accessKeys.map((key) => (
                <label key={key} className="flex items-center gap-2 rounded-lg border border-white/10 bg-[#132035] px-3 py-2 text-sm">
                  <Checkbox
                    checked={Boolean(sections.access[key as keyof typeof sections.access])}
                    onCheckedChange={(checked) => updateSection("access", { [key]: Boolean(checked) } as Partial<SurveySections["access"]>)}
                  />
                  {REMOTE_SURVEY.accessLabels[key]}
                </label>
              ))}
            </div>
            <div>
              <Label className="text-white/70">Restrictions</Label>
              <Textarea
                className="mt-1 bg-[#132035] border-white/10 text-white"
                value={sections.access.restrictions}
                onChange={(e) => updateSection("access", { restrictions: e.target.value })}
                placeholder="Narrow drive, locked side gate, parking, pets…"
              />
            </div>
          </section>
        )}

        {sectionKey === "loft" && (
          <section className="space-y-4">
            <h1 className="font-display text-2xl font-semibold">Loft</h1>
            <Label className="text-white/70 mb-2 block">Is the loft accessible?</Label>
            <YesNo name="loft" value={loftAccessible} onChange={(v) => updateSection("loft", { accessible: v })} />
            <FieldError message={fieldErrors.loft_accessible} />
            {loftAccessible === false && (
              <div className="space-y-3">
                <select
                  className="w-full rounded-md border border-white/10 bg-[#132035] px-3 py-2 text-sm"
                  value={sections.loft.not_accessible_reason ?? ""}
                  onChange={(e) => updateSection("loft", { not_accessible_reason: e.target.value || null })}
                >
                  <option value="">Why not?</option>
                  {REMOTE_SURVEY.loftReasons.map((reason) => (
                    <option key={reason} value={reason}>{REMOTE_SURVEY.loftReasonLabels[reason]}</option>
                  ))}
                </select>
                <Textarea
                  className="bg-[#132035] border-white/10 text-white"
                  value={sections.loft.not_accessible_note}
                  onChange={(e) => updateSection("loft", { not_accessible_note: e.target.value })}
                  placeholder="Anything the installer should know"
                />
              </div>
            )}
          </section>
        )}

        {sectionKey === "electrical" && (
          <section className="space-y-4">
            <h1 className="font-display text-2xl font-semibold">Electrical</h1>
            {REMOTE_SURVEY.existingEquipment.map((item) => {
              const field = item.field as "existing_solar" | "existing_battery" | "existing_ev_heat_pump";
              return (
                <div key={field}>
                  <Label className="text-white/70 mb-2 block">{item.label}?</Label>
                  <YesNo
                    name={field}
                    value={asBool(sections.electrical[field])}
                    onChange={(v) => updateSection("electrical", { [field]: v } as Partial<SurveySections["electrical"]>)}
                  />
                  <FieldError message={fieldErrors[field]} />
                </div>
              );
            })}
            {existingGeneration && (
              <Textarea
                className="bg-[#132035] border-white/10 text-white"
                value={sections.electrical.existing_generation_note}
                onChange={(e) => updateSection("electrical", { existing_generation_note: e.target.value })}
                placeholder="What is already installed?"
              />
            )}
          </section>
        )}

        {sectionKey === "battery" && (
          <section className="space-y-4">
            <h1 className="font-display text-2xl font-semibold">Battery location</h1>
            <select
              className="w-full rounded-md border border-white/10 bg-[#132035] px-3 py-2 text-sm"
              value={sections.battery.location ?? ""}
              onChange={(e) => updateSection("battery", { location: e.target.value || null })}
            >
              <option value="">Proposed location</option>
              {REMOTE_SURVEY.batteryLocations.map((loc) => (
                <option key={loc} value={loc}>{REMOTE_SURVEY.batteryLocationLabels[loc]}</option>
              ))}
            </select>
            <FieldError message={fieldErrors.battery_location} />
            {([
              ["dry", "Dry"],
              ["ventilated", "Ventilated"],
              ["accessible", "Accessible for maintenance"],
              ["not_living_accommodation", "Not living accommodation"],
              ["combustibles_nearby", "Combustibles nearby"],
              ["heat_source_nearby", "Heat source nearby"],
            ] as const).map(([field, label]) => (
              <div key={field}>
                <Label className="text-white/70 mb-2 block">{label}</Label>
                <YesNo name={field} value={asBool(sections.battery[field])} onChange={(v) => updateSection("battery", { [field]: v })} />
                <FieldError message={fieldErrors[field]} />
              </div>
            ))}
          </section>
        )}

        {sectionKey === "inverter" && (
          <section className="space-y-4">
            <h1 className="font-display text-2xl font-semibold">Inverter location</h1>
            <select
              className="w-full rounded-md border border-white/10 bg-[#132035] px-3 py-2 text-sm"
              value={sections.inverter.preferred_location ?? ""}
              onChange={(e) => updateSection("inverter", { preferred_location: e.target.value })}
            >
              <option value="">Proposed location</option>
              {REMOTE_SURVEY.batteryLocations.map((loc) => (
                <option key={loc} value={loc}>{REMOTE_SURVEY.batteryLocationLabels[loc]}</option>
              ))}
            </select>
            <FieldError message={fieldErrors.preferred_location} />
            <Label className="text-white/70 mb-2 block">Indoor or outdoor?</Label>
            <div className="grid grid-cols-2 gap-2">
              {["indoor", "outdoor"].map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => updateSection("inverter", { indoor_outdoor: option })}
                  className={`rounded-lg border px-3 py-2 text-sm capitalize ${
                    sections.inverter.indoor_outdoor === option
                      ? "border-[#29ffa9] bg-[#29ffa9]/10 text-[#29ffa9]"
                      : "border-white/15 text-white/70"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
            <FieldError message={fieldErrors.indoor_outdoor} />
          </section>
        )}

        {sectionKey === "system" && (
          <section className="space-y-4">
            <h1 className="font-display text-2xl font-semibold">System & energy</h1>
            <div className="rounded-xl border border-white/10 bg-[#132035] p-4 text-sm space-y-1 text-white/70">
              {hideDeclaration ? (
                <p>System size is set when this survey is attached to a quote.</p>
              ) : (
                <>
                  <p>{data?.prefill.systemCapacityKw ?? "—"} kWp solar</p>
                  <p>{data?.prefill.batteryKwh ? `${data.prefill.batteryKwh} kWh battery` : "No battery"}</p>
                  <p>{data?.prefill.includesEv ? "EV charger included" : "No EV charger"}</p>
                </>
              )}
              <p>Usage {data?.prefill.annualConsumptionKwh ?? "—"} kWh / year</p>
              <p>Meter {data?.prefill.meterNumber ?? "—"}</p>
              <p>{[data?.prefill.currentSupplier, data?.prefill.currentTariff].filter(Boolean).join(" · ") || "Supplier not set"}</p>
            </div>
            {!hideDeclaration && (
              <>
                <Label className="text-white/70 mb-2 block">Is this still correct?</Label>
                <YesNo name="system" value={asBool(sections.system.confirmed)} onChange={(v) => updateSection("system", { confirmed: v })} />
                <FieldError message={fieldErrors.system_confirmed} />
                {sections.system.confirmed === false && (
                  <p className="text-sm text-white/50">Add a note at the end so the installer can follow up.</p>
                )}
              </>
            )}
          </section>
        )}

        {sectionKey === "declaration" && (
          <section className="space-y-4">
            <h1 className="font-display text-2xl font-semibold">Declaration</h1>
            {REMOTE_SURVEY.declarationStatements.map((statement) => (
              <label key={statement.key} className="flex items-start gap-3 rounded-lg border border-white/10 bg-[#132035] p-3 text-sm">
                <Checkbox
                  checked={Boolean(sections.declaration[statement.key as keyof typeof sections.declaration])}
                  onCheckedChange={(checked) =>
                    updateSection("declaration", { [statement.key]: Boolean(checked) } as Partial<SurveySections["declaration"]>)
                  }
                />
                {statement.text}
              </label>
            ))}
            <label className="flex items-start gap-3 rounded-lg border border-white/10 bg-[#132035] p-3 text-sm">
              <Checkbox
                checked={sections.declaration.accepted}
                onCheckedChange={(checked) => updateSection("declaration", { accepted: Boolean(checked) })}
              />
              I confirm these statements and submit this survey.
            </label>
            <FieldError message={fieldErrors.declaration} />
            <div>
              <Label className="text-white/70">Anything else we should know?</Label>
              <Textarea
                className="mt-1 bg-[#132035] border-white/10 text-white"
                value={notes}
                onChange={(e) => {
                  dirtyRef.current = true;
                  setNotes(e.target.value);
                }}
                placeholder="Flat roof, three-phase, leasehold, listed building…"
              />
            </div>
          </section>
        )}

        {photoTypes.length > 0 && (
          <section className="space-y-3">
            <div>
              <h2 className="text-sm font-semibold text-white/80">Photos <span className="font-normal text-white/45">(optional)</span></h2>
              <p className="text-xs text-white/50 mt-1">{photoUploadRequirements()}</p>
            </div>
            <FieldError message={fieldErrors.photos} />
            {photoTypes.map((photoType) => (
              <SurveyPhotoSlot
                key={photoType}
                photoType={photoType as PhotoType}
                image={imagesByType.get(photoType)}
                remaining={remaining}
                requiredCount={requiredCount}
                disabled={frozen || !canEditCurrent}
                onUpload={async (file, capturePath: CapturePath) => {
                  await uploadMutation.mutateAsync({ file, photoType, capturePath });
                  trackSurveyPhoto(trackingId, photoType, capturePath);
                }}
                onRetake={async () => {
                  const image = imagesByType.get(photoType);
                  if (image) await deleteMutation.mutateAsync(image.id);
                }}
              />
            ))}
          </section>
        )}
      </main>

      <footer className="fixed bottom-0 inset-x-0 border-t border-white/8 bg-[#050d1a]/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl gap-3 px-4 py-3">
          <Button
            type="button"
            variant="outline"
            className="border-white/20 text-white/80"
            disabled={sectionIndex === 0}
            onClick={() => void goBack()}
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          {sectionKey === wizardSections[wizardSections.length - 1] && (hideDeclaration || sectionKey === "declaration") ? (
            <Button className="btn-cta flex-1" disabled={frozen || submitMutation.isPending} onClick={() => void handleSubmit()}>
              {submitMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              {hideDeclaration
                ? (alreadySubmitted ? "Update field capture" : "Submit field capture")
                : (alreadySubmitted ? "Update survey" : "Submit survey")}
            </Button>
          ) : (
            <Button className="btn-cta flex-1" disabled={saveMutation.isPending} onClick={() => void goNext()}>
              Next <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
}
